import { RateType } from '@m/constants/Rate';
import { DurationRate, FlatRate, RateBaseV2, TabularRateBase } from '@m/types/api';

import { convertNumberToDollarFormat } from '@m/utils/convertNumberToDollarFormat';
import { isDurationRate, isFlatRate, isTabularRate } from '@m/utils/guards/rateTypes';
import { millisecondsToCleanPeriod } from '@m/utils/timeFormatters';

type ParsedPeriod = {
  dynamicPriceText?: string;
  interval: number;
  periodMs: number;
  price: number;
  priceText: string | null;
  priceWithFee: number;
  priceWithFeeText: string | null;
  unit: string;
  unitText: string; // adds `s` if plural
};

export type ParsedFlatRate = {
  freeMinutes: number;
  maxPeriod: ParsedPeriod;
  type: RateType.Flat;
};

export type ParsedDurationRate = {
  freeMinutes: number;
  maxPeriod: ParsedPeriod;
  period: ParsedPeriod;
  type: RateType.Duration;
};

export type ParsedTabularRateStep = {
  cumulativePeriodInterval: number;
  cumulativePeriodUnit: string;
  cumulativePeriodUnitText: string; // adds `s` if plural
  dynamicPriceText?: string;
  price: number;
  priceText: string | null;
  priceWithFee: number;
  priceWithFeeText: string | null;
};

export type ParsedTabularRate = {
  freeMinutes: number;
  tabularSteps: ParsedTabularRateStep[];
  type: RateType.Tabular;
};

export const getPriceRange = (priceA: number, priceB: number) => {
  const toDollar = (amount: number) =>
    convertNumberToDollarFormat({
      price: amount,
      includeZeros: true,
    });

  return `${toDollar(priceA)} - ${toDollar(priceB)}`;
};

export function parseFlatRate(
  rate: FlatRate,
  options?: {
    priceIncludeZeros?: boolean;
  },
): ParsedFlatRate {
  const { priceIncludeZeros = false } = options || {};
  const { freePeriod, maxPeriod, priceWithFee, maxPrice, maxCost, minCost } = rate;
  const maxPeriodFormatted = millisecondsToCleanPeriod(maxPeriod);

  let returnObj: ParsedFlatRate = {
    type: RateType.Flat,
    freeMinutes: freePeriod / 1000 / 60,
    maxPeriod: {
      price: maxPrice,
      priceWithFee,
      priceText: convertNumberToDollarFormat({
        price: maxPrice,
        includeZeros: priceIncludeZeros,
      }),
      priceWithFeeText: convertNumberToDollarFormat({
        price: priceWithFee,
        includeZeros: priceIncludeZeros,
      }),
      periodMs: maxPeriod,
      interval: Number(maxPeriodFormatted.interval),
      unit: maxPeriodFormatted.unit,
      unitText:
        maxPeriodFormatted.interval === '1'
          ? maxPeriodFormatted.unit
          : `${maxPeriodFormatted.unit}s`,
    },
  };

  if (minCost !== undefined && maxCost !== undefined && minCost !== null && maxCost !== null) {
    returnObj = {
      ...returnObj,
      maxPeriod: {
        ...returnObj.maxPeriod,
        dynamicPriceText: getPriceRange(minCost, maxCost),
      },
    };
  }

  return returnObj;
}

export function parseDurationRate(
  rate: DurationRate,
  options?: {
    priceIncludeZeros?: boolean;
  },
): ParsedDurationRate {
  const { priceIncludeZeros = false } = options || {};
  const {
    freePeriod,
    maxPeriod,
    maxPrice,
    period,
    pricePerPeriod,
    pricePerPeriodWithFee,
    maxCostPerPeriod,
    minCostPerPeriod,
    maxPriceWithFee,
  } = rate;

  const periodFormatted = millisecondsToCleanPeriod(period);
  const maxPeriodFormatted = millisecondsToCleanPeriod(maxPeriod);

  let returnObj: ParsedDurationRate = {
    type: RateType.Duration,
    freeMinutes: freePeriod / 1000 / 60,
    period: {
      price: pricePerPeriod,
      priceWithFee: pricePerPeriodWithFee,
      priceText: convertNumberToDollarFormat({
        price: pricePerPeriod,
        includeZeros: priceIncludeZeros,
      }),
      priceWithFeeText: convertNumberToDollarFormat({
        price: pricePerPeriodWithFee,
        includeZeros: priceIncludeZeros,
      }),
      periodMs: period,
      interval: Number(periodFormatted.interval),
      unit: periodFormatted.unit,
      unitText:
        periodFormatted.interval === '1' ? periodFormatted.unit : `${periodFormatted.unit}s`,
    },
    maxPeriod: {
      price: maxPrice,
      priceWithFee: maxPriceWithFee,
      priceText: convertNumberToDollarFormat({
        price: maxPrice,
        includeZeros: priceIncludeZeros,
      }),
      priceWithFeeText: convertNumberToDollarFormat({
        price: maxPriceWithFee,
        includeZeros: priceIncludeZeros,
      }),
      periodMs: maxPeriod,
      interval: Number(maxPeriodFormatted.interval),
      unit: maxPeriodFormatted.unit,
      unitText:
        maxPeriodFormatted.interval === '1'
          ? maxPeriodFormatted.unit
          : `${maxPeriodFormatted.unit}s`,
    },
  };

  if (maxCostPerPeriod && minCostPerPeriod) {
    returnObj = {
      ...returnObj,
      period: {
        ...returnObj.period,
        dynamicPriceText: getPriceRange(minCostPerPeriod, maxCostPerPeriod),
      },
    };
  }

  return returnObj;
}

export function parseTabularRate(
  rate: TabularRateBase,
  options?: {
    priceIncludeZeros?: boolean;
  },
): ParsedTabularRate {
  const { priceIncludeZeros = false } = options || {};
  const { freePeriod, tabularSteps } = rate;
  return {
    type: RateType.Tabular,
    freeMinutes: freePeriod / 1000 / 60,
    tabularSteps: tabularSteps.map(
      ({ amount, amountWithFee, cumulativePeriod, minCost, maxCost }) => {
        const cumulativePeriodFormatted = millisecondsToCleanPeriod(cumulativePeriod);

        let tabStepReturnObj: ParsedTabularRateStep = {
          price: amount,
          priceWithFee: amountWithFee,
          priceText: convertNumberToDollarFormat({
            price: amount,
            includeZeros: priceIncludeZeros,
          }),
          priceWithFeeText: convertNumberToDollarFormat({
            price: amountWithFee,
            includeZeros: priceIncludeZeros,
          }),
          cumulativePeriodInterval: Number(cumulativePeriodFormatted.interval),
          cumulativePeriodUnit: cumulativePeriodFormatted.unit,
          cumulativePeriodUnitText:
            cumulativePeriodFormatted.interval === '1'
              ? cumulativePeriodFormatted.unit
              : `${cumulativePeriodFormatted.unit}s`,
        };

        if (
          minCost !== undefined &&
          maxCost !== undefined &&
          minCost !== null &&
          maxCost !== null
        ) {
          tabStepReturnObj = {
            ...tabStepReturnObj,
            dynamicPriceText: getPriceRange(minCost, maxCost),
          };
        }
        return tabStepReturnObj;
      },
    ),
  };
}

/**
 * Check if a rate has dynamic pricing enabled
 * Dynamic pricing is enabled when minCost and maxCost fields are present
 */
export const hasDynamicPricing = (rate: RateBaseV2 | null | undefined): boolean => {
  if (!rate) return false;

  if (isDurationRate(rate)) {
    return (
      rate.minCostPerPeriod !== undefined &&
      rate.minCostPerPeriod !== null &&
      rate.maxCostPerPeriod !== undefined &&
      rate.maxCostPerPeriod !== null
    );
  }

  if (isFlatRate(rate)) {
    return (
      rate.minCost !== undefined &&
      rate.minCost !== null &&
      rate.maxCost !== undefined &&
      rate.maxCost !== null
    );
  }

  if (isTabularRate(rate)) {
    return rate.tabularSteps.some(
      (step) =>
        step.minCost !== undefined &&
        step.minCost !== null &&
        step.maxCost !== undefined &&
        step.maxCost !== null,
    );
  }

  return false;
};

export const getRateLineItems = (rate: RateBaseV2) => {
  if (isDurationRate(rate)) {
    const {
      period: { priceText, interval, unitText, dynamicPriceText },
      maxPeriod: { priceText: maxPriceText, interval: maxInterval, unitText: maxUnitText },
    } = parseDurationRate(rate, { priceIncludeZeros: true });

    return [
      [`Per ${interval === 1 ? '' : interval} ${unitText}`, dynamicPriceText ?? priceText],
      [`Max charge for ${maxInterval === 1 ? '' : maxInterval} ${maxUnitText}`, maxPriceText],
    ];
  }

  if (isFlatRate(rate)) {
    const {
      maxPeriod: { price, priceText, interval, unitText, dynamicPriceText },
    } = parseFlatRate(rate, { priceIncludeZeros: true });

    const left = `Cost for ${interval} ${unitText}`;
    let right;

    if (dynamicPriceText) right = dynamicPriceText;
    else if (price === 0) right = 'Free';
    else right = priceText;

    return [[left, right]];
  }

  if (isTabularRate(rate)) {
    const { tabularSteps } = parseTabularRate(rate, { priceIncludeZeros: true });
    const lineItems = tabularSteps.map((step) => {
      const {
        price,
        priceText,
        cumulativePeriodInterval,
        cumulativePeriodUnitText,
        dynamicPriceText,
      } = step;

      const left = `Up to ${cumulativePeriodInterval} ${cumulativePeriodUnitText}`;
      let right;

      if (dynamicPriceText) right = dynamicPriceText;
      else if (price === 0) right = 'Free';
      else right = priceText;

      return [left, right];
    });

    return lineItems;
  }

  return [];
};
